<template>
  <MglGeojsonLayer
    :source-id="source.id"
    :source="source.json"
    :layer-id="layer.id"
    :layer="layer.json"
    @click="onClick"
    @mouseenter="onMouseEnter"
    @mouseleave="onMouseLeave"
  />
</template>

<script>
import axios from 'axios'
import Mapbox from 'mapbox-gl'
import { MglGeojsonLayer } from 'vue-mapbox'

import { EventBus } from '../../eventBus'

const TRACK_COLOURS = new Array('#00FF00', '#1100FF', '#00FFFF', '#F7FF00', '#FF0000', '#FF00E2', '#994A00', '#138500', '#730085', '#FF007A', '#FDBF6F', '#1F78B4', '#000000', '#FFFFFF')

export default {
  name: 'YachtLayer',
  inject: ['mapbox', 'map', 'actions'],
  components: {
    MglGeojsonLayer,
  },
  props: {
    raceId: {
      type: Number,
      default: 0,
    },
    raceTime: {
      type: String,
      default: '',
    },
    fleetData: {
      type: Array,
      default: () => [],
    },
    showYachtNames: {
      type: Boolean,
      default: true,
    },
  },
  data() {
    return {
      loading: 'true',
      trackLayers: [],
      layer: {
        id: 'yachts',
        json: {
          type: 'symbol',
          source: 'yachts-source',
          filter: ['==', 'toggle', 1],
          layout: {
            'icon-image': '{imagename}',
            'icon-allow-overlap': true,
          },
        },
      },
    }
  },
  computed: {
    source() {
      const yachtFeatures = []
      if (!this.loading) {
        for (let i = 0; i < this.fleetData.length; i++) {
          const yacht = this.fleetData[i]
          if (yacht.Position !== null && yacht.ShowOnMap) {
            const lonLat = [yacht.Position.Longitude, yacht.Position.Latitude]
            const feature = {
              type: 'symbol',
              geometry: {
                type: 'Point',
                coordinates: lonLat,
              },
              properties: {
                title: yacht.Name,
                icon: 'circle',
                imagename: this.getImageName(yacht),
                id: yacht.RaceEntryId,
                data: yacht,
                toggle: 1,
              },
            }

            yachtFeatures.push(feature)
          }
        }
      }

      return {
        id: 'yachts-source',
        json: {
          type: 'geojson',
          data: {
            type: 'FeatureCollection',
            features: yachtFeatures,
          },
          cluster: false,
        },
      }
    },
  },
  watch: {
    raceTime: function(val, oldVal) {
      this.resetYachtTracks()
    },
    showYachtNames: function(val, oldVal) {
      this.toggleYachtNames()
    },
  },
  async created() {
    const styles = [
      'fleet_blue',
      'record_orange',
      'leader_red',
      'divleader_green',
      'retired',
    ]
    const directions = ['N', 'NE', 'NW', 'S', 'SE', 'SW']

    for (const style in styles) {
      for (const dir in directions) {
        const imageName = `${styles[style]}_${directions[dir]}`
        const imagePath = `/tracker/TrackerIcons/${imageName}.png`
        await this.loadImage(imageName, imagePath)
      }
    }

    this.loading = false
  },
  mounted() {
    EventBus.$on('showAllYachtTracks', () => {
      this.showAllYachtTracks()
    })

    EventBus.$on('clearAllYachtTracks', () => {
      this.resetYachtTracks()
    })

    EventBus.$on('yachtRowClicked', (raceEntryId) => {
      EventBus.$emit('clearAllYachtTracks')

      const yacht = this.fleetData.find(
        (yacht) => yacht.RaceEntryId === raceEntryId
      )
      if (yacht.Position) {
        this.map.flyTo({
          center: [yacht.Position.Longitude, yacht.Position.Latitude],
        })
      }

      this.showYachtTrack(raceEntryId)
    })

    /* add yacht names */
    this.map.addLayer({
      id: 'yacht-names',
      type: 'symbol',
      source: 'yachts-source',
      layout: {
        visibility: 'none', // set to none on start
        'symbol-placement': 'point',
        'text-font': ['Klokantech Noto Sans Regular'],
        'text-field': '{title}', // '{title}', // part 2 of this is how to do it
        'text-size': 12,
        'text-offset': [0, 1.5],
      },
      paint: {
        'text-color': '#263b59',
        'text-halo-color': '#fff',
        'text-halo-width': 2,
      },
    })

    this.toggleYachtNames()
  },
  destroyed() {
    EventBus.$emit('clearAllYachtTracks')
  },
  methods: {
    loadImage(name, url) {
      const vm = this
      return new Promise((resolve, reject) => {
        vm.map.loadImage(url, (error, image) => {
          if (error) {
            reject()
          }
          if (!vm.map.hasImage(name)) {
            vm.map.addImage(name, image)
          }
          resolve()
        })
      })
    },
    getImageName(yacht) {
      let imageName = 'fleet_blue'
      if (yacht.IsLineHonourRecord === true) {
        imageName = 'record_orange'
      }
 else {
        for (let i = 0; i < yacht.Categories.length; i++) {
          const cat = yacht.Categories[i]
          if (cat.CatPlace == 1 && cat.Name == 'Line Honours') {
            imageName = 'leader_red'
            break
          }
 else if (cat.DivPlace == 1) imageName = 'divleader_green'
        }
      }
      if (yacht.Position.IsRetired == true) imageName = 'retired'

      const direction = yacht.Position.Heading

      return imageName + '_' + direction
    },
    onClick(event) {
      EventBus.$emit('clearAllYachtTracks')

      const raceEntryId = event.mapboxEvent.features[0].properties.id
      const coords = event.mapboxEvent.lngLat

      this.showYachtTrack(raceEntryId)
      this.map.flyTo({ center: event.mapboxEvent.lngLat })
      EventBus.$emit('yachtClicked', raceEntryId)
    },
    onMouseEnter(e) {
      this.map.getCanvas().style.cursor = 'pointer'
    },
    onMouseLeave(e) {
      this.map.getCanvas().style.cursor = ''
    },
    showYachtTrack(raceEntryId) {
      const yacht = this.fleetData.find(
        (yacht) => yacht.RaceEntryId === raceEntryId
      )

      if (!yacht) return

      axios
        .get(
          `${window.BASE_FEEDS_URL}Yachts/Track/${
            this.raceId
          }/${raceEntryId}?raceTime=${this.raceTime}`
        )
        .then((response) => {
          const data = response.data

          const coords = []

          for (let i = 0; i < data.length; i++) {
            coords.push([data[i].Longitude, data[i].Latitude])
          }

          if (yacht.Position && yacht.Position.Latitude) {
            coords.push([yacht.Position.Longitude, yacht.Position.Latitude])
          }

          this.addTrackToMap(yacht.RaceEntryId, coords)
        })
        .catch((error) => {
          this.errored = true
          // eslint-disable-next-line
          console.log(error);
        })
    },
    addTrackToMap(raceEntryId, coords, lineColour = undefined) {
      const layerId = 'track' + raceEntryId
      
      const layer = this.map.getLayer(layerId)
      const source = this.map.getSource(layerId)

      if (layer !== undefined) {
        this.map.removeLayer(layerId)
      }

      if (source !== undefined) {
        this.map.removeSource(layerId)
      }

      this.trackLayers.push(layerId)

      this.map.addLayer({
        id: layerId,
        type: 'line',
        source: {
          type: 'geojson',
          data: {
            type: 'Feature',
            properties: {},
            geometry: {
              type: 'LineString',
              coordinates: coords || [],
            },
          },
        },
        layout: {
          'line-join': 'round',
          'line-cap': 'round',
        },
        paint: {
          'line-color': lineColour ? lineColour : '#00FF00',
          'line-width': 2,
        },
      },
      'yachts') // insert below yachts layer
    },
    showAllYachtTracks() {
      //https://feeds.cycaracing.com/yachts/track/152
      let url = `${window.BASE_FEEDS_URL}Yachts/Track/${this.raceId}`

      if (this.raceTime) {
        //https://feeds.cycaracing.com/yachts/track/152?raceTime=20211227075000
        url+= `?raceTime=${this.raceTime}`
      }

      axios.get(url).then((response) => {
          if (response.status !== 200) {
            throw new Error('Something went wrong with loading tracks, status not 200')
          }

          if (Object.keys(response.data).length === 0) {
            throw new Error('Tracks response is empty, race may not have started yet')
          }

          const data = response.data

          for (let i = 0; i < this.fleetData.length; i++) {
            const raceEntryId = this.fleetData[i].RaceEntryId

            if (data[`${raceEntryId}`] == false) {
              throw new Error(`Could not find track for ${raceEntryId}`)
            }

            const coords = data[`${raceEntryId}`]

            this.addTrackToMap(raceEntryId, coords, TRACK_COLOURS[i % TRACK_COLOURS.length])
          }
        })
        .catch((error) => {
          this.errored = true
          // eslint-disable-next-line
          console.log(error);
        })
    },
    resetYachtTracks() {
      this.trackLayers.forEach((trackLayerId) => {
        const layer = this.map.getLayer(trackLayerId)
        if (layer !== undefined) this.map.removeLayer(trackLayerId)
        const source = this.map.getSource(trackLayerId)
        if (source !== undefined) this.map.removeSource(trackLayerId)
      })

      this.trackLayers = []
    },
    toggleYachtNames() {
      if (this.showYachtNames) {
        this.map.setLayoutProperty('yacht-names', 'visibility', 'visible')
      }
 else {
        this.map.setLayoutProperty('yacht-names', 'visibility', 'none')
      }
    },
  },
}
</script>

<style lang="less">
.yacht-popup {
  // font-family: Din_reg,Arial;
}
</style>
